import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import Logger from '@/utils/logger';

interface ExchangeRateContextType {
  exchangeRate: number;
  lastUpdate: Date | null;
  isLoading: boolean;
  error: string | null;
  refreshRate: () => Promise<void>;
}

const ExchangeRateContext = createContext<ExchangeRateContextType | undefined>(undefined);

interface ExchangeRateProviderProps {
  children: ReactNode;
}

export const ExchangeRateProvider: React.FC<ExchangeRateProviderProps> = ({ children }) => {
  const [exchangeRate, setExchangeRate] = useState<number>(1.0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExchangeRate = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', { setting_key: 'usd_to_usdt_rate' });

      if (error || !data) {
        Logger.warn('Failed to fetch exchange rate, using default:', error);
        setExchangeRate(1.0);
        setError('Failed to fetch current rate');
        return;
      }

      const rate = parseFloat(data) || 1.0;
      setExchangeRate(rate);
      setLastUpdate(new Date());
      Logger.debug('Exchange rate updated:', rate);
    } catch (error) {
      Logger.error('Exchange rate fetch error:', error);
      setError('Network error');
      setExchangeRate(1.0);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshRate = async () => {
    await fetchExchangeRate();
  };

  // Fetch rate on mount and then every 2 minutes
  useEffect(() => {
    fetchExchangeRate();
    
    const interval = setInterval(fetchExchangeRate, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(interval);
  }, []);

  const value: ExchangeRateContextType = {
    exchangeRate,
    lastUpdate,
    isLoading,
    error,
    refreshRate
  };

  return (
    <ExchangeRateContext.Provider value={value}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

export const useExchangeRate = (): ExchangeRateContextType => {
  const context = useContext(ExchangeRateContext);
  if (context === undefined) {
    throw new Error('useExchangeRate must be used within an ExchangeRateProvider');
  }
  return context;
};